/**
 * Product Categories for Purpose Collective
 * These must match the database enum exactly
 */

export const PRODUCT_CATEGORIES = [
  'Accessories',
  'Active & Sport',
  'Beauty',
  'Beachwear',
  'Coats & Jackets',
  'Denim',
  'Dresses',
  'Home',
  'Intimates & Nightwear',
  'Jeans',
  'Jewelry',
  'Knitwear',
  'Outerwear',
  'Pants',
  'Shoes',
  'Shorts',
  'Skirts',
  'Suits',
  'Tops',
  'Travel'
] as const

export type ProductCategory = typeof PRODUCT_CATEGORIES[number]

/**
 * Category detection keywords for automated categorization during scraping
 */
export const CATEGORY_KEYWORDS: Record<ProductCategory, string[]> = {
  'Outerwear': [
    'jacket', 'coat', 'blazer', 'parka', 'windbreaker', 'raincoat', 
    'trench', 'bomber', 'vest', 'gilet', 'anorak', 'cape', 'poncho', 'outerwear'
  ],
  'Coats & Jackets': [
    'coat', 'jacket', 'blazer', 'parka', 'trench', 'bomber', 'peacoat',
    'overcoat', 'raincoat', 'windbreaker', 'puffer', 'quilted', 'leather jacket',
    'denim jacket', 'sport coat', 'suit jacket'
  ],
  'Knitwear': [
    'sweater', 'jumper', 'pullover', 'cardigan', 'knit', 'knitwear',
    'turtleneck', 'crew neck', 'cable knit', 'cashmere sweater', 'wool sweater',
    'merino sweater', 'chunky knit', 'fine knit', 'roll neck', 'knitted'
  ],
  'Tops': [
    'shirt', 't-shirt', 'tee', 'blouse', 'top', 'tank', 'camisole', 
    'polo', 'henley', 'tunic', 'crop top', 'bodysuit', 'button-down',
    'vest top', 'singlet', 'halter'
  ],
  'Pants': [
    'pants', 'trousers', 'slacks', 'chinos', 'cargo', 'palazzo',
    'wide-leg', 'straight-leg', 'tapered', 'cropped pants', 'capri',
    'culottes', 'joggers', 'sweatpants', 'track pants'
  ],
  'Jeans': [
    'jeans', 'denim pants', 'skinny jeans', 'straight jeans', 'bootcut',
    'flare jeans', 'mom jeans', 'boyfriend jeans', 'wide leg jeans',
    'high-waisted jeans', 'low-rise jeans'
  ],
  'Denim': [
    'denim', 'jean jacket', 'denim jacket', 'denim shirt', 'chambray',
    'denim dress', 'denim skirt', 'denim shorts', 'jean', 'dungarees'
  ],
  'Shorts': [
    'shorts', 'short', 'bermuda', 'cargo shorts', 'denim shorts',
    'athletic shorts', 'bike shorts', 'board shorts', 'high-waisted shorts'
  ],
  'Skirts': [
    'skirt', 'mini skirt', 'midi skirt', 'maxi skirt', 'pencil skirt',
    'a-line skirt', 'pleated skirt', 'wrap skirt', 'circle skirt',
    'denim skirt', 'leather skirt', 'tulle skirt'
  ],
  'Suits': [
    'suit', 'suits', 'blazer set', 'suit jacket', 'suit pants', 'two-piece suit',
    'three-piece suit', 'business suit', 'formal suit', 'tuxedo', 'dinner jacket',
    'matching set', 'co-ord', 'coordinates', 'power suit', 'pantsuit', 'skirt suit'
  ],
  'Dresses': [
    'dress', 'gown', 'maxi dress', 'midi dress', 'mini dress', 
    'cocktail dress', 'evening gown', 'sundress', 'shift dress', 
    'wrap dress', 'bodycon', 'shirtdress', 'slip dress', 'formal dress',
    'casual dress', 'party dress', 'day dress'
  ],
  'Beachwear': [
    'swimsuit', 'bikini', 'swimwear', 'beachwear', 'swimming', 'swim',
    'one-piece', 'two-piece', 'tankini', 'swimming trunks', 'board shorts',
    'beach cover-up', 'sarong', 'kaftan', 'beach dress', 'rash guard',
    'swim shorts', 'swimming costume'
  ],
  'Active & Sport': [
    'activewear', 'sportswear', 'athletic', 'yoga', 'running', 
    'training', 'gym', 'workout', 'sports bra', 'compression', 
    'performance', 'moisture-wicking', 'track', 'cycling', 'tennis',
    'golf', 'ski', 'swim', 'swimsuit', 'bikini', 'rashguard',
    'running shorts', 'athletic shorts', 'gym shorts', 'sports shorts'
  ],
  'Intimates & Nightwear': [
    'underwear', 'lingerie', 'bra', 'panties', 'briefs', 'boxers', 
    'thermal', 'pajamas', 'pyjamas', 'nightgown', 'sleepwear', 
    'nightwear', 'robe', 'bathrobe', 'loungewear', 'nightshirt',
    'camisole', 'slip', 'shapewear', 'intimate'
  ],
  'Home': [
    'bedding', 'pillow', 'cushion', 'throw', 'blanket', 'duvet', 
    'sheet', 'towel', 'curtain', 'rug', 'mat', 'furniture', 'decor', 
    'candle', 'lamp', 'vase', 'kitchen', 'dining', 'bathroom', 
    'storage', 'organizer', 'home', 'living', 'interior'
  ],
  'Beauty': [
    'makeup', 'cosmetics', 'skincare', 'skin care', 'skin', 'foundation', 'lipstick', 'mascara',
    'eyeshadow', 'concealer', 'bronzer', 'blush', 'highlighter', 'primer',
    'serum', 'moisturizer', 'cleanser', 'toner', 'sunscreen', 'spf',
    'face mask', 'eye cream', 'retinol', 'vitamin c', 'hyaluronic acid',
    'perfume', 'fragrance', 'cologne', 'body lotion', 'body wash', 'shampoo',
    'conditioner', 'hair mask', 'hair oil', 'styling', 'nail polish', 'beauty',
    'treatment', 'essence', 'ampoule', 'facial', 'exfoliant', 'exfoliator',
    'anti-aging', 'anti aging', 'wrinkle', 'acne', 'blemish', 'pore',
    'brightening', 'hydrating', 'nourishing', 'rejuvenating', 'optimizer',
    'biome', 'probiotic', 'peptide', 'collagen', 'elastin', 'vita', 'vitamin',
    'mineral', 'botanical', 'extract', 'oil', 'balm', 'cream', 'gel', 'lotion',
    'face', 'facial', 'complexion', 'glow', 'radiance', 'luminous',
    'deodorant', 'antiperspirant', 'deo', 'body spray', 'body mist', 'aluminum-free',
    'aluminum free', 'personal care', 'hygiene', 'body care', 'grooming'
  ],
  'Accessories': [
    'bag', 'backpack', 'purse', 'wallet', 'belt', 'scarf', 'hat', 
    'cap', 'beanie', 'gloves', 'mittens', 'sunglasses', 'eyewear', 
    'watch', 'tie', 'bow tie', 'socks', 'tights', 'stockings', 
    'umbrella', 'tech accessories', 'phone case', 'accessory'
  ],
  'Jewelry': [
    'necklace', 'bracelet', 'ring', 'earring', 'pendant', 'chain', 
    'charm', 'anklet', 'brooch', 'pin', 'cufflinks', 'jewelry', 
    'jewellery', 'gold', 'silver', 'diamond', 'gemstone', 'pearl'
  ],
  'Shoes': [
    'shoe', 'sneaker', 'boot', 'sandal', 'heel', 'flat', 'loafer', 
    'oxford', 'pump', 'wedge', 'espadrille', 'mule', 'clog', 
    'slipper', 'trainer', 'runner', 'hiking boot', 'ankle boot', 
    'knee-high', 'footwear', 'slides', 'sneakers', 'boots', 'shoes',
    'canvas sneaker', 'leather boot', 'running shoe', 'athletic shoe'
  ],
  'Travel': [
    'luggage', 'suitcase', 'carry-on', 'duffel', 'weekender', 
    'travel bag', 'packing cube', 'passport', 'travel accessory', 
    'toiletry bag', 'dopp kit', 'travel pillow', 'neck pillow', 
    'travel organizer', 'backpack', 'travel', 'journey'
  ]
}

/**
 * Detect category from product title and description
 */
export function detectCategory(
  title: string, 
  description: string = '',
  brand: string = ''
): ProductCategory {
  const searchText = `${title} ${description} ${brand}`.toLowerCase()
  const titleLower = title.toLowerCase()
  
  // Special handling for beauty products - check for strong beauty indicators
  const beautyIndicators = ['skin', 'serum', 'moisturizer', 'cream', 'facial', 'face', 
                           'vitamin', 'vita', 'biome', 'optimizer', 'treatment', 'essence',
                           'cleanser', 'toner', 'spf', 'sunscreen', 'makeup', 'cosmetic',
                           'perfume', 'fragrance', 'beauty', 'skincare', 'anti-aging',
                           'deodorant', 'antiperspirant', 'aluminum-free', 'body care', 'personal care'];
  
  const hasBeautyIndicator = beautyIndicators.some(indicator => 
    titleLower.includes(indicator) || 
    (description && description.toLowerCase().includes(indicator))
  );
  
  // Score each category based on keyword matches
  const categoryScores: Record<ProductCategory, number> = {} as any
  
  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    let score = 0
    
    for (const keyword of keywords) {
      // Check for exact word match (with word boundaries)
      const regex = new RegExp(`\\b${keyword.toLowerCase()}\\b`, 'gi')
      const matches = searchText.match(regex)
      
      if (matches) {
        // Higher score for matches in title
        const titleMatches = title.toLowerCase().match(regex)
        score += titleMatches ? titleMatches.length * 3 : 0
        
        // Lower score for matches in description
        const descMatches = description.toLowerCase().match(regex)
        score += descMatches ? descMatches.length * 1 : 0
      }
    }
    
    // Boost Beauty category score if beauty indicators are present
    if (category === 'Beauty' && hasBeautyIndicator) {
      score += 10; // Strong boost for beauty products
    }
    
    categoryScores[category as ProductCategory] = score
  }
  
  // Find the category with the highest score
  let bestCategory: ProductCategory = 'Accessories' // Default
  let highestScore = 0
  
  for (const [category, score] of Object.entries(categoryScores)) {
    if (score > highestScore) {
      highestScore = score
      bestCategory = category as ProductCategory
    }
  }
  
  // If no keywords matched, try some basic heuristics
  if (highestScore === 0) {
    // Check for common patterns
    if (searchText.includes('dress')) return 'Dresses'
    if (searchText.includes('shirt') || searchText.includes('top')) return 'Tops'
    if (searchText.includes('pant')) return 'Pants'
    if (searchText.includes('jean')) return 'Jeans'
    if (searchText.includes('short')) return 'Shorts'
    if (searchText.includes('skirt')) return 'Skirts'
    if (searchText.includes('suit')) return 'Suits'
    if (searchText.includes('shoe') || searchText.includes('sneaker')) return 'Shoes'
    if (searchText.includes('bag') || searchText.includes('backpack')) return 'Accessories'
  }
  
  return bestCategory
}

/**
 * Get category description for UI display
 */
export const CATEGORY_DESCRIPTIONS: Record<ProductCategory, string> = {
  'Accessories': 'Bags, belts, scarves, and fashion accessories',
  'Active & Sport': 'Athletic wear and performance clothing',
  'Beauty': 'Skincare, makeup, fragrances, and personal care',
  'Beachwear': 'Swimwear and beach essentials',
  'Coats & Jackets': 'Outerwear including coats, jackets, and blazers',
  'Denim': 'All denim clothing and accessories',
  'Dresses': 'Dresses for all occasions',
  'Home': 'Home decor, bedding, and living essentials',
  'Intimates & Nightwear': 'Underwear, lingerie, and sleepwear',
  'Jeans': 'Denim pants and jeans',
  'Jewelry': 'Necklaces, rings, bracelets, and fine accessories',
  'Knitwear': 'Sweaters, cardigans, and knitted garments',
  'Outerwear': 'General outer layer clothing',
  'Pants': 'Trousers, slacks, and pants',
  'Shoes': 'Footwear of all types',
  'Shorts': 'Short pants and shorts',
  'Skirts': 'Skirts of all lengths and styles',
  'Suits': 'Formal suits, matching sets, and coordinates',
  'Tops': 'Shirts, blouses, and upper body wear',
  'Travel': 'Luggage, travel bags, and journey essentials'
}